import React, { useEffect, useState } from 'react';
import { DeleteOutlined, DownloadOutlined, FileExcelOutlined, InboxOutlined, PlusOutlined, QrcodeOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Col, Input, Modal, Row, Select, Space, Table, Tag, Tooltip } from 'antd';
import Dropzone from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ContentHeader } from '../../components';
import { menuHelper } from '../../helpers/account';
import { layDsLoaiTaiSan } from '../../services/danhmuc';
import { Links } from '../../helpers/Links';
import { TaiSan, TrangThaiTaiSan } from '../../models/taisan/TaiSan';
import { DataSelectBox } from '../../models/selectbox';
import { timKiemDSTaiSan, xoaTaiSan, layDsTaiSanPhu, xuatExcelDanhSachTaiSan } from '../../services/taisan';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import DonViSelectTree from '../utils/DonViSelectTree';
import InTemQRTaiSan from './InTemQRTaiSan';

const DanhSachTaiSan = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const menu: any = menuHelper.checkMultiPermission([Links.TaiSan.ChiTiet, Links.TaiSan.Xem]);
    const [loading, setLoading] = useState<boolean>(false);
    const [dsTaiSan, setDsTaiSan] = useState<TaiSan[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    // Filter
    const [tuKhoa, setTuKhoa] = useState<string>("");
    const [idLoaiTaiSan, setIdLoaiTaiSan] = useState<number | undefined>();
    const [idDonVi, setIdDonVi] = useState<number | undefined>(undefined);
    const [trangThai, setTrangThai] = useState<number | undefined>();

    // In QR
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [showModalInQR, setShowModalInQR] = useState<boolean>(false);
    const [inToanBo, setInToanBo] = useState<boolean>(false);

    // Quản lý tài sản phụ
    const [childAssetsMap, setChildAssetsMap] = useState<{ [id: number]: TaiSan[] }>({});
    const [loadingChildren, setLoadingChildren] = useState<{ [id: number]: boolean }>({});

    // SelectBox data
    const [optionLoaiTaiSan, setOptionLoaiTaiSan] = useState<DataSelectBox[]>([]);

    const trangThaiOptions = [
        { value: TrangThaiTaiSan.MoiNhap, label: 'Mới nhập' },
        { value: TrangThaiTaiSan.DangSuDung, label: 'Đang sử dụng' },
        { value: TrangThaiTaiSan.BaoTri, label: 'Bảo trì' },
        { value: TrangThaiTaiSan.Hong, label: 'Hỏng' },
        { value: TrangThaiTaiSan.ChoThanhLy, label: 'Chờ thanh lý' },
        { value: TrangThaiTaiSan.DieuChuyen, label: 'Điều chuyển' },
        { value: TrangThaiTaiSan.DaThanhLy, label: 'Đã thanh lý' },
    ];

    const getTrangThaiTag = (trangThai: number) => {
        switch (trangThai) {
            case TrangThaiTaiSan.MoiNhap:
                return <Tag color="blue">Mới nhập</Tag>;
            case TrangThaiTaiSan.DangSuDung:
                return <Tag color="green">Đang sử dụng</Tag>;
            case TrangThaiTaiSan.BaoTri:
                return <Tag color="orange">Bảo trì</Tag>;
            case TrangThaiTaiSan.Hong:
                return <Tag color="red">Hỏng</Tag>;
            case TrangThaiTaiSan.ChoThanhLy:
                return <Tag color="purple">Chờ thanh lý</Tag>;
            case TrangThaiTaiSan.DieuChuyen:
                return <Tag color="cyan">Điều chuyển</Tag>;
            case TrangThaiTaiSan.DaThanhLy:
                return <Tag color="red">Đã thanh lý</Tag>;
            default:
                return <Tag>Không xác định</Tag>;
        }
    };

    const handleExpand = async (expanded: boolean, record: TaiSan) => {
        if (expanded && record.id && !childAssetsMap[record.id]) {
            try {
                setLoadingChildren(prev => ({ ...prev, [record.id!]: true }));
                const children = await layDsTaiSanPhu(record.id);
                setChildAssetsMap(prev => ({ ...prev, [record.id!]: children }));
            } catch (error) {
                console.error(error);
            } finally {
                setLoadingChildren(prev => ({ ...prev, [record.id!]: false }));
            }
        }
    };

    const columns = [
        {
            title: 'STT',
            dataIndex: 'stt',
            key: 'stt',
            width: 60,
            render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
        },
        {
            title: 'Tên tài sản',
            dataIndex: 'tenTaiSan',
            key: 'tenTaiSan',
            width: 260,
            render: (ten: string, record: TaiSan) => (
                <div>
                    <span style={{ fontWeight: (record.soLuongTaiSanPhu || 0) > 0 ? 600 : 'normal' }}>
                        {ten}
                    </span>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                        Mã HRM: {record.ma} {record.maKeToan ? ` | FAST: ${record.maKeToan}` : ''}
                    </div>
                    {(record.soLuongTaiSanPhu || 0) > 0 && (
                        <Tag color="geekblue" style={{ marginTop: 2, fontSize: '11px' }}>
                            {record.soLuongTaiSanPhu} tài sản phụ
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: 'Loại tài sản',
            dataIndex: 'loaiTaiSan',
            key: 'loaiTaiSan',
            width: 150,
        },
        {
            title: 'Số lượng',
            dataIndex: 'soLuong',
            key: 'soLuong',
            width: 90,
            align: 'center' as 'center',
        },
        {
            title: 'Đơn vị',
            dataIndex: 'tenDonVi',
            key: 'tenDonVi',
            width: 180,
        },
        {
            title: 'Người sử dụng',
            dataIndex: 'nguoiSuDung',
            key: 'nguoiSuDung',
            width: 150,
        },
        {
            title: 'Vị trí',
            dataIndex: 'viTriSuDung',
            key: 'viTriSuDung',
            width: 140,
        },
        {
            title: 'Trạng thái',
            dataIndex: 'trangThai',
            key: 'trangThai',
            width: 130,
            render: (trangThai: number) => getTrangThaiTag(trangThai),
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 100,
            align: "center" as const,
            fixed: 'right' as 'right',
            render: (_: any, record: TaiSan) => (
                <div>
                    {menu[Links.TaiSan.Xem] && (
                        <Link to={`${Links.TaiSan.Xem}?id=${record.id}`} className="hrm-btn hrm-btn-sm btn-primary">
                            Xem
                        </Link>
                    )}
                </div>
            ),
        },
    ];

    const loadDanhSach = async () => {
        try {
            setLoading(true);
            const result = await timKiemDSTaiSan(
                {
                    tuKhoa,
                    idLoaiTaiSan,
                    idDonVi,
                    trangThai,
                },
                (currentPage - 1) * pageSize,
                pageSize
            );
            setDsTaiSan(result.data);
            setTotal(result.recordsTotal);
        } catch (error: any) {
            toast.error('Lỗi khi tải danh sách tài sản: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadDataSelectBox = async () => {
        try {
            const loaiTaiSan = await layDsLoaiTaiSan();
            setOptionLoaiTaiSan(loaiTaiSan);
        } catch (error: any) {
            toast.error('Lỗi khi tải dữ liệu: ' + error.message);
        }
    };

    const handleTimKiem = () => {
        setCurrentPage(1);
        loadDanhSach();
    };

    const handleInQR = () => {
        if (selectedRowKeys.length === 0) {
            toast.warning('Vui lòng chọn ít nhất một tài sản để in QR');
            return;
        }
        setInToanBo(false);
        setShowModalInQR(true);
    };

    const handleInQRToanBo = () => {
        setInToanBo(true);
        setShowModalInQR(true);
    };

    const [exporting, setExporting] = useState<'TSCD' | 'CCDC' | null>(null);
    const [importModalType, setImportModalType] = useState<'TSCD' | 'CCDC' | null>(null);
    const [fileUpload, setFileUpload] = useState<File | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);

    const handleXuatExcel = async (phanLoai: 'TSCD' | 'CCDC') => {
        try {
            setExporting(phanLoai);
            await xuatExcelDanhSachTaiSan({ tuKhoa, idLoaiTaiSan, idDonVi, trangThai }, phanLoai);
        } catch (error: any) {
            toast.error('Lỗi khi xuất excel: ' + (error?.message || ''));
        } finally {
            setExporting(null);
        }
    };

    const handleImportExcel = async () => {
        if (!fileUpload) return;
        setUploading(true);
        dispatch(showLoadingScreen());
        await new Promise((resolve) => setTimeout(resolve, 2000));
        dispatch(hideLoadingScreen());
        setUploading(false);
        setImportModalType(null);
        setFileUpload(null);
    };

    useEffect(() => {
        loadDataSelectBox();
    }, []);

    useEffect(() => {
        loadDanhSach();
    }, [currentPage, pageSize]);

    return (
        <section>
            <ContentHeader title="Quản lý tài sản" />
            <Row className="form-group" gutter={[8, 8]}>
                <Col xs={24} sm={12} md={5}>
                    <Input
                        placeholder="Tìm kiếm theo mã, tên, sê-ri, vị trí tài sản..."
                        value={tuKhoa}
                        onChange={(e) => setTuKhoa(e.target.value)}
                        onPressEnter={handleTimKiem}
                        prefix={<SearchOutlined />}
                    />
                </Col>
                <Col xs={24} sm={12} md={5}>
                    <DonViSelectTree value={idDonVi} allowClear autoSelectFirst={false} onChange={(e: any) => { console.log(e.target.value); setIdDonVi(e.target.value) }} />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <Select
                        allowClear
                        placeholder="Loại tài sản"
                        style={{ width: '100%' }}
                        options={optionLoaiTaiSan}
                        value={idLoaiTaiSan}
                        onChange={(value) => setIdLoaiTaiSan(value)}
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <Select
                        allowClear
                        placeholder="Trạng thái"
                        style={{ width: '100%' }}
                        options={trangThaiOptions}
                        value={trangThai}
                        onChange={(value) => setTrangThai(value)}
                    />
                </Col>
                <Col xs={24} sm={24} md={8} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleTimKiem}>Tìm kiếm</Button>
                    {menu[Links.TaiSan.ChiTiet] && (
                        <Link className="hrm-btn hrm-btn-warning" to={Links.TaiSan.ChiTiet}>
                            <span style={{ alignItems: 'center' }}><PlusOutlined /></span>
                            Thêm mới
                        </Link>
                    )}
                </Col>
                <Col xs={24} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <Button
                        type="default"
                        icon={<QrcodeOutlined />}
                        onClick={handleInQR}
                        disabled={selectedRowKeys.length === 0}
                    >
                        In QR đã chọn ({selectedRowKeys.length})
                    </Button>
                    <Button type="default" icon={<QrcodeOutlined />} onClick={handleInQRToanBo}>
                        In QR toàn bộ
                    </Button>
                    <Button
                        type="primary"
                        className="hrm-btn hrm-btn-success"
                        icon={<DownloadOutlined />}
                        loading={exporting === 'TSCD'}
                        disabled={exporting !== null}
                        onClick={() => handleXuatExcel('TSCD')}
                    >
                        Xuất excel TSCĐ
                    </Button>
                    <Button
                        type="primary"
                        className="hrm-btn hrm-btn-success"
                        icon={<DownloadOutlined />}
                        loading={exporting === 'CCDC'}
                        disabled={exporting !== null}
                        onClick={() => handleXuatExcel('CCDC')}
                    >
                        Xuất excel CCDC
                    </Button>
                    <Button
                        type="primary"
                        className="hrm-btn hrm-btn-primary"
                        icon={<UploadOutlined />}
                        onClick={() => {
                            setFileUpload(null);
                            setImportModalType('TSCD');
                        }}
                    >
                        Import excel TSCĐ
                    </Button>
                    <Button
                        type="primary"
                        className="hrm-btn hrm-btn-primary"
                        icon={<UploadOutlined />}
                        onClick={() => {
                            setFileUpload(null);
                            setImportModalType('CCDC');
                        }}
                    >
                        Import excel CCDC
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <div className="card">
                        <div className="card-body">
                            <Table
                                columns={columns}
                                dataSource={dsTaiSan}
                                loading={loading}
                                rowKey="id"
                                rowSelection={{
                                    selectedRowKeys,
                                    onChange: (keys) => setSelectedRowKeys(keys),
                                    columnWidth: 48,
                                    fixed: true,
                                }}
                                scroll={{ x: 1400 }}
                                expandable={{
                                    expandedRowRender: (record: TaiSan) => (
                                        <div style={{ padding: '8px 16px', background: '#fafafa', borderRadius: 4 }}>
                                            <div style={{ fontWeight: 600, marginBottom: 8, color: '#1890ff' }}>
                                                Danh sách tài sản phụ thuộc [{record.ma} - {record.tenTaiSan}]:
                                            </div>
                                            <Table
                                                size="small"
                                                pagination={false}
                                                loading={loadingChildren[record.id!]}
                                                dataSource={childAssetsMap[record.id!] || []}
                                                rowKey="id"
                                                columns={[
                                                    {
                                                        title: 'STT',
                                                        width: 50,
                                                        align: 'center',
                                                        render: (_: any, __: any, idx: number) => idx + 1,
                                                    },
                                                    {
                                                        title: 'Mã tài sản phụ',
                                                        dataIndex: 'ma',
                                                        key: 'ma',
                                                        width: 140,
                                                        render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
                                                    },
                                                    {
                                                        title: 'Tên tài sản phụ',
                                                        dataIndex: 'tenTaiSan',
                                                        key: 'tenTaiSan',
                                                    },
                                                    {
                                                        title: 'Loại tài sản',
                                                        dataIndex: 'loaiTaiSan',
                                                        key: 'loaiTaiSan',
                                                        width: 140,
                                                    },
                                                    {
                                                        title: 'Số lượng',
                                                        dataIndex: 'soLuong',
                                                        key: 'soLuong',
                                                        width: 80,
                                                        align: 'center',
                                                    },
                                                    {
                                                        title: 'Vị trí',
                                                        dataIndex: 'viTriSuDung',
                                                        key: 'viTriSuDung',
                                                        width: 120,
                                                    },
                                                    {
                                                        title: 'Trạng thái',
                                                        dataIndex: 'trangThai',
                                                        key: 'trangThai',
                                                        width: 130,
                                                        render: (tt: number) => getTrangThaiTag(tt),
                                                    },
                                                    {
                                                        title: 'Thao tác',
                                                        key: 'action',
                                                        width: 80,
                                                        align: 'center',
                                                        render: (_: any, subRecord: TaiSan) => (
                                                            <div>
                                                                {menu[Links.TaiSan.Xem] && (
                                                                    <Link to={`${Links.TaiSan.Xem}?id=${record.id}`} className="hrm-btn hrm-btn-sm btn-primary">
                                                                        Xem
                                                                    </Link>
                                                                )}
                                                            </div>
                                                        ),
                                                    },
                                                ]}
                                            />
                                        </div>
                                    ),
                                    rowExpandable: (record: TaiSan) => (record.soLuongTaiSanPhu || 0) > 0,
                                    onExpand: handleExpand,
                                }}
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: total,
                                    showSizeChanger: true,
                                    showTotal: (total) => `Tổng số: ${total}`,
                                    onChange: (page, size) => {
                                        setCurrentPage(page);
                                        setPageSize(size);
                                    },
                                }}
                            />
                        </div>
                    </div>
                </Col>
            </Row>

            <InTemQRTaiSan 
                show={showModalInQR}
                idsTaiSan={selectedRowKeys.map(k => Number(k))}
                inToanBo={inToanBo}
                filter={{
                    tuKhoa,
                    idLoaiTaiSan,
                    idDonVi,
                    trangThai,
                }}
                onClose={() => {
                    setShowModalInQR(false);
                    setInToanBo(false);
                    setSelectedRowKeys([]);
                }}
            />

            <Modal
                title={
                    <Space>
                        <FileExcelOutlined style={{ color: '#217346', fontSize: 18 }} />
                        <span>
                            {importModalType === 'TSCD'
                                ? 'Import dữ liệu Tài sản cố định (TSCĐ)'
                                : 'Import dữ liệu Công cụ dụng cụ (CCDC)'}
                        </span>
                    </Space>
                }
                open={importModalType !== null}
                onCancel={() => {
                    if (!uploading) {
                        setImportModalType(null);
                        setFileUpload(null);
                    }
                }}
                footer={[
                    <Button
                        key="back"
                        disabled={uploading}
                        onClick={() => {
                            setImportModalType(null);
                            setFileUpload(null);
                        }}
                    >
                        Hủy
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={uploading}
                        disabled={!fileUpload}
                        icon={<UploadOutlined />}
                        onClick={handleImportExcel}
                    >
                        Tải lên
                    </Button>,
                ]}
                destroyOnClose
                centered
            >
                <div style={{ padding: '8px 0' }}>
                    <Dropzone
                        maxFiles={1}
                        multiple={false}
                        accept={{
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                            'application/vnd.ms-excel': ['.xls'],
                        }}
                        onDrop={(acceptedFiles) => {
                            if (acceptedFiles && acceptedFiles.length > 0) {
                                setFileUpload(acceptedFiles[0]);
                            }
                        }}
                    >
                        {({ getRootProps, getInputProps, isDragActive }) => (
                            <div
                                {...getRootProps()}
                                className="hrm-dropzone-uploadfile"
                                style={{
                                    padding: '28px 16px',
                                    background: isDragActive ? '#e6f4ff' : '#fafafa',
                                    borderColor: isDragActive ? '#1677ff' : fileUpload ? '#28a745' : '#d9d9d9',
                                    borderRadius: 8,
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <input {...getInputProps()} />
                                {isDragActive ? (
                                    <div>
                                        <p style={{ marginBottom: 8 }}>
                                            <InboxOutlined style={{ color: '#1677ff', fontSize: 42 }} />
                                        </p>
                                        <p style={{ fontSize: 14, fontWeight: 500, color: '#1677ff', margin: 0 }}>
                                            Thả tệp Excel vào đây...
                                        </p>
                                    </div>
                                ) : fileUpload ? (
                                    <div>
                                        <p style={{ marginBottom: 8 }}>
                                            <FileExcelOutlined style={{ color: '#217346', fontSize: 40 }} />
                                        </p>
                                        <p style={{ fontSize: 14, fontWeight: 600, color: '#262626', margin: '0 0 4px 0', wordBreak: 'break-all' }}>
                                            {fileUpload.name}
                                        </p>
                                        <p style={{ fontSize: 12, color: '#8c8c8c', margin: '0 0 8px 0' }}>
                                            Kích thước: {(fileUpload.size / 1024).toFixed(1)} KB
                                        </p>
                                        <Button
                                            size="small"
                                            danger
                                            disabled={uploading}
                                            icon={<DeleteOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFileUpload(null);
                                            }}
                                        >
                                            Chọn tệp khác
                                        </Button>
                                    </div>
                                ) : (
                                    <div>
                                        <p style={{ marginBottom: 8 }}>
                                            <InboxOutlined style={{ color: '#1677ff', fontSize: 42 }} />
                                        </p>
                                        <p style={{ fontSize: 14, fontWeight: 500, color: '#262626', margin: '0 0 4px 0' }}>
                                            Nhấp hoặc kéo thả tệp Excel vào đây để tải lên
                                        </p>
                                        <p style={{ fontSize: 12, color: '#8c8c8c', margin: 0 }}>
                                            Hỗ trợ định dạng tệp .xlsx, .xls
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </Dropzone>
                </div>
            </Modal>
        </section>
    );
};

export default DanhSachTaiSan;
